import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../assistant/markdown';
import { inAppPath } from '../lib/inAppPath';
import { substituteProduct } from './unread';

/**
 * How an announcement body renders (docs/42 §7, steward-app#2612). Modelled on
 * `firmMarkdownComponents`, NOT on the assistant's `markdownComponents` `a`, which opens ANY
 * non-in-app href in a new tab with no scheme check and keeps images.
 *
 * An announcement is written by an operator, so the threat is smaller than a model answer
 * steered by a prospect. It is still held to the strict rules, because the body is markdown
 * from a store that more than one tool will write to:
 *
 *  - an in-app path (the `inAppPath` rule) goes through the router;
 *  - an EXTERNAL link is a link only when it is `https:`, and opens in a new tab with
 *    `noopener noreferrer`. `javascript:`, `data:`, `http:`, `mailto:`, `//host` and `/\host` all
 *    render as their plain text, with no href;
 *  - an image renders as its alt text, whatever the src (an image loads with no click);
 *  - raw HTML is never rendered as elements: no `rehype-raw` is installed, and `skipHtml` drops
 *    the node outright.
 */

/** The https URL a link may point at, or null. Strict on the RAW string first: the URL parser
 *  strips tabs and newlines and trims, so `ht\ttps://x` would otherwise parse as https. */
export function safeExternalHref(raw: string | undefined): string | null {
  if (!raw || !/^https:\/\/[^\s\\]/i.test(raw)) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && url.hostname ? url.href : null;
  } catch {
    return null;
  }
}

export const announcementMarkdownComponents = {
  ...markdownComponents,
  a: ({ href, children }: React.ComponentPropsWithoutRef<'a'>) => {
    const path = inAppPath(href);
    if (path) return <Link to={path} className="text-bronze-text underline underline-offset-2">{children}</Link>;
    const external = safeExternalHref(href);
    if (external) {
      return (
        <a href={external} target="_blank" rel="noopener noreferrer" className="text-bronze-text underline underline-offset-2">
          {children}
        </a>
      );
    }
    return <span>{children}</span>;
  },
  img: ({ alt }: React.ComponentPropsWithoutRef<'img'>) => <span>{alt ?? ''}</span>,
};

/** The slice of an mdast node the substitution needs. */
interface MdNode { type: string; value?: string; alt?: string | null; children?: MdNode[] }

/**
 * A remark plugin that replaces `{product}` in TEXT nodes, after parsing. Substituting into the
 * markdown source instead would let a firm product name holding `*` or `_` be read as emphasis.
 * Code and inline code are left alone: their value is not a text node. An image's `alt` is
 * substituted too, because the `img` override renders it as text.
 */
export function remarkProductName() {
  return () => (tree: MdNode) => {
    const walk = (node: MdNode) => {
      if (node.type === 'text' && typeof node.value === 'string') node.value = substituteProduct(node.value);
      if (node.type === 'image' && typeof node.alt === 'string') node.alt = substituteProduct(node.alt);
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

export function AnnouncementBody({ body }: { body: string }) {
  return (
    <ReactMarkdown
      skipHtml
      remarkPlugins={[remarkGfm, remarkProductName()]}
      components={announcementMarkdownComponents}
    >
      {body}
    </ReactMarkdown>
  );
}
